import { useRef } from 'react';
import { Box, CircularProgress, Backdrop } from '@mui/material';
import { motion } from 'framer-motion';

import PhoneVerificationPanel from './components/PhoneVerificationPanel';
import PurchaseForm from './components/PurchaseForm';
import { GuideSection } from './components/GuideSection';
import { ValidationErrorPanel } from './components/ValidationErrorPanel';
import { SuccessDialog } from './components/SuccessDialog';
import { useApplicationForm } from './hooks/useApplicationForm';
import { useAuthentication } from './hooks/useAuthentication';

// gradient color palette
const gradientBg = 'linear-gradient(135deg, #0ea5e9 0%, #3b82f6 55%, #8b5cf6 100%)';

const ApplicationPage = () => {
  const formRef = useRef<HTMLDivElement | null>(null);
  
  // 커스텀 훅으로 상태 관리
  const {
    form,
    setForm,
    agreed,
    setAgreed,
    isAuthenticated,
    setIsAuthenticated,
    isSubmitting,
    submitSuccess,
    blocking,
    setBlocking,
    showSuccessDialog,
    setShowSuccessDialog,
    validationErrors,
    handleSubmit,
  } = useApplicationForm();

  const scrollToForm = () => {
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth' }), 300);
  };

  // 인증 관련 로직을 커스텀 훅으로 처리
  useAuthentication({
    setForm,
    setAgreed,
    setIsAuthenticated,
    setBlocking,
    scrollToForm,
  });

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: gradientBg,
        position: 'relative',
        py: { xs: 3, md: 8 },
        px: { xs: 0, md: 0 },
        // 입체감 있는 floating blur 요소 추가
        overflow: 'hidden'
      }}
    >
      {/* 동적 blur, floating element */}
      <Box
        component={motion.div}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 0.18, scale: 1 }}
        transition={{ duration: 1.2 }}
        sx={{
          position: 'absolute',
          top: { xs: '-70px', md: '-140px' },
          left: { xs: '-90px', md: '-120px' },
          width: { xs: 180, md: 320 },
          height: { xs: 180, md: 320 },
          background: 'linear-gradient(135deg, #e0e7ff 0%, #dbeafe 100%)',
          filter: 'blur(65px)',
          borderRadius: '50%',
          zIndex: 0,
        }}
      />
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 80 }}
        animate={{ opacity: 0.13, y: 0 }}
        transition={{ duration: 1.3 }}
        sx={{
          position: 'absolute',
          bottom: { xs: '-90px', md: '-160px' },
          right: { xs: '-80px', md: '-140px' },
          width: { xs: 170, md: 300 },
          height: { xs: 170, md: 300 },
          background: 'linear-gradient(135deg, #a5b4fc 0%, #f0abfc 100%)',
          filter: 'blur(70px)',
          borderRadius: '50%',
          zIndex: 0,
        }}
      />

      {/* Backdrop (로딩/완료) */}
      <Backdrop open={blocking} sx={{ zIndex: 1300, color: '#fff', backdropFilter: 'blur(3px)' }}>
        <Box display="flex" flexDirection="column" alignItems="center">
          <CircularProgress color="inherit" />
          <Box mt={2} fontWeight="bold" letterSpacing={1} sx={{
            background: 'linear-gradient(90deg, #0ea5e9 0%, #8b5cf6 100%)',
            WebkitBackgroundClip: 'text', color: 'transparent', fontSize: 20
          }}>
            {submitSuccess ? '✅ 신청 완료!' : '처리 중입니다...'}
          </Box>
        </Box>
      </Backdrop>

      {/* 안내/가이드 카드 */}
      <GuideSection />

      {/* 본인인증/신청폼 */}
      <Box sx={{ position: 'relative', zIndex: 3, width: '100%', maxWidth: 920, mx: 'auto', px: { xs: 0, md: 2 } }}>
        {!isAuthenticated ? (
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
          >
            <PhoneVerificationPanel
              onVerified={(info) => {
                setForm(prev => ({ ...prev, name: info.name, phone: info.phone }));
                setAgreed(true);
                setIsAuthenticated(true);
              }}
              blocking={blocking}
              setBlocking={setBlocking}
            />
          </motion.div>
        ) : (
          <Box ref={formRef} sx={{ width: '100%' }}>
            {/* 유효성 검사 에러 메시지 표시 */}
            <ValidationErrorPanel errors={validationErrors} />
            
            <motion.div
              initial={{ opacity: 0, scale: 0.97, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <PurchaseForm
                form={form}
                setForm={setForm}
                onSubmit={handleSubmit}
                isSubmitting={isSubmitting}
              />
            </motion.div>

          </Box>
        )}
        {/* Footer 제거 — apply 페이지 디자인과 어울리지 않음 */}
      </Box>

      {/* 완료 Dialog */}
      <SuccessDialog 
        open={showSuccessDialog} 
        onClose={() => setShowSuccessDialog(false)} 
      />
    </Box>
  );
};

export default ApplicationPage;
